// Words and phrases used across screens, status names, and the API's fixed error messages.
import type { subscriptionEn } from './subscription.js'

export const commonEn = {
  'common.interfaceLanguage': 'Interface language',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.close': 'Close',
  'common.search': 'Search',
  'common.select': 'Select…',
  'common.loading': 'Loading…',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.none': 'None',
  'common.all': 'All',
  'common.somethingWentWrong': 'Something went wrong',
  'common.requestFailed': 'Request failed ({status})',
  'common.untitled': 'Untitled',
  'common.editingLanguage': 'Editing language',
  'common.primary': 'Primary',
  'common.translated': 'translated',
  'common.notTranslated': 'not translated',
  'common.fallbackNote': 'Empty {code} fields fall back to {language} on the site.',
  'meta.blocks': '{count} block|{count} blocks',
  'meta.order': 'Order {n}',
  'meta.journal': 'Journal',
  'meta.news': 'News',
  'meta.material': 'Material',
  'meta.finish': 'Finish',
  'apiError.categoryHasProducts': 'Move or delete the {n} product(s) in this category first',
  'apiError.typeNotAllowed': '{type} is not allowed',
  'apiError.fileUsed': 'This file is used in {count} place|This file is used in {count} places',
  'apiError.notFound': 'Not found',
  'apiError.planLimit.pages': 'Your plan allows up to {limit} pages',
  'apiError.planLimit.users': 'Your plan allows up to {limit} users',
  'apiError.planLimit.languages': 'Your plan allows up to {limit} content languages',
  'apiError.planLimit.items': 'Your plan allows up to {limit} catalogue items',
  'apiError.planLimit.articles': 'Your plan allows up to {limit} articles',
  'apiError.planLimit.storage': 'Your plan allows up to {limit} MB of media storage. Delete unused files or upgrade.',
  'apiError.planFeature': 'Your plan does not include {feature}',
  'apiError.planArea': 'Not in your package: this part of the CMS is not included in your plan',
  'apiError.chatHeld': '{name} already took this chat',
  'apiError.chatNotHolder': 'Take over the chat first',
  'apiError.chatClosed': 'This chat is closed',
  'apiError.chatGone': 'This chat no longer exists (chats are kept 7 days)',
  'apiError.chatUnreachable': 'The chat could not be reached. Try again in a moment.',
  'apiError.tooManyRequests': 'Too many requests. Please try again in a minute.',
  'apiError.tooManySignIns': 'Too many sign-in attempts. Try again in a minute.',
  'apiError.ssoInvalid': 'This sign-in link is invalid or has expired',
  'apiError.suspended': 'This site is suspended. Please contact your account manager.',
  'apiError.ai.noKey': 'AI writing needs an Anthropic API key. An administrator can add it in Settings.',
  'apiError.ai.unreadableKey': 'The saved Anthropic API key cannot be read. Enter it again in Settings.',
  'apiError.ai.daily': 'The daily AI writing limit for this site is reached. Try again tomorrow.',
  'apiError.ai.perUser': 'Too many AI writing requests. Wait a minute.',
  'apiError.ai.language': 'That language is not enabled on this site.',
  'apiError.ai.badKey': 'Anthropic rejected the API key. Check it in Settings.',
  'apiError.ai.noCredit': 'The Anthropic account has no credit left. Top up at console.anthropic.com.',
  'apiError.ai.rateLimited': 'Anthropic is receiving too many requests from this key. Try again in a minute.',
  'apiError.ai.overloaded': 'Anthropic is overloaded. Try again shortly.',
  'apiError.ai.timeout': 'Anthropic did not answer in time. Try again.',
  'apiError.ai.network': 'Could not reach Anthropic. Try again.',
  'apiError.ai.refused': 'The AI declined this request. Rephrase the text or brief.',
  'apiError.ai.badRequest': 'Anthropic could not process this request.',
  'apiError.ai.failed': 'The AI writing request failed. Try again.',
  'status.draft': 'Draft',
  'status.published': 'Published',
  'status.archived': 'Archived',
  'role.admin': 'Admin',
  'role.editor': 'Editor',
  'format.bytes.b': '{n} B',
  'format.bytes.kb': '{n} KB',
  'format.bytes.mb': '{n} MB',
  'apiError.mediaNotFound': 'Media not found',
  'apiError.someFieldsInvalid': 'Some fields are invalid',
  'apiError.fileTooLarge': 'File is larger than 15 MB',
  'apiError.inquiryNotFound': 'Inquiry not found',
  'apiError.crossOrigin': 'Cross-origin request rejected',
  'apiError.slugUsed': 'That slug is already used',
  'apiError.missingFile': 'Missing `file` field',
  'apiError.currentPasswordWrong': 'Current password is incorrect',
  'apiError.svgScripts': 'SVG contains scripts or event handlers',
  'apiError.fileTypeMismatch': 'File content does not match its type',
  'apiError.expectedMultipart': 'Expected multipart/form-data',
  'apiError.signInRequired': 'Sign in required',
  'apiError.sessionExpired': 'Session expired',
  'apiError.badCredentials': 'Email or password is incorrect',
  'apiError.tooManyAttempts': 'Too many attempts. Try again in 15 minutes.',
  'apiError.captchaFailed': 'The captcha check failed. Try again.',
  'apiError.captchaNotConfigured': 'Sign-in is unavailable: the captcha is not configured',
  'apiError.userNotFound': 'User not found',
  'apiError.unknownPlatform': 'Unknown platform',
  'apiError.typeNotFound': 'Content type not found',
  'apiError.sectionAddressTaken': 'That address is already used by a section',
  'apiError.pageAddressTaken': 'That address is already used by a page',
  'apiError.sectionLimit': 'You can add up to 10 sections',
  'apiError.sectionBuiltinAddress': 'The address of a built-in section cannot change',
  'apiError.sectionBuiltinDelete': 'Built-in sections can be switched off but not deleted',
  'apiError.sectionNotFound': 'Section not found',
  'apiError.categoryHasItems': 'Move or delete the {n} item(s) in this category first',
  'apiError.noPermission': 'You do not have permission to do this',
  'apiError.noPublish': 'You do not have permission to publish',
  'apiError.adminOnly': 'Only an administrator can manage administrators',
  'apiError.ownRole': 'You cannot change your own role',
  'apiError.roleNotFound': 'Role not found',
  'apiError.roleExists': 'A role with this name already exists',
  'apiError.roleInUse': 'This role is still assigned to users',
  'apiError.internalEndpoint': 'Internal endpoint',
  'apiError.adminRequired': 'Admin role required',
  'apiError.selfDemote': 'You cannot deactivate or demote yourself',
  'apiError.emailExists': 'A user with this email already exists',
} as const

export const commonId: Record<keyof typeof commonEn, string> = {
  'common.interfaceLanguage': 'Bahasa antarmuka',
  'common.save': 'Simpan',
  'common.saving': 'Menyimpan…',
  'common.cancel': 'Batal',
  'common.delete': 'Hapus',
  'common.edit': 'Ubah',
  'common.close': 'Tutup',
  'common.search': 'Cari',
  'common.select': 'Pilih…',
  'common.loading': 'Memuat…',
  'common.yes': 'Ya',
  'common.no': 'Tidak',
  'common.none': 'Tidak ada',
  'common.all': 'Semua',
  'common.somethingWentWrong': 'Terjadi kesalahan',
  'common.requestFailed': 'Permintaan gagal ({status})',
  'common.untitled': 'Tanpa judul',
  'common.editingLanguage': 'Bahasa penyuntingan',
  'common.primary': 'Utama',
  'common.translated': 'diterjemahkan',
  'common.notTranslated': 'belum diterjemahkan',
  'common.fallbackNote': 'Kolom {code} yang kosong memakai {language} di situs.',
  'meta.blocks': '{count} blok',
  'meta.order': 'Urutan {n}',
  'meta.journal': 'Jurnal',
  'meta.news': 'Berita',
  'meta.material': 'Bahan',
  'meta.finish': 'Finishing',
  'apiError.categoryHasProducts': 'Pindahkan atau hapus dulu {n} produk di kategori ini',
  'apiError.typeNotAllowed': '{type} tidak diizinkan',
  'apiError.fileUsed': 'File ini digunakan di {count} tempat',
  'apiError.notFound': 'Tidak ditemukan',
  'apiError.planLimit.pages': 'Paket Anda mengizinkan hingga {limit} halaman',
  'apiError.planLimit.users': 'Paket Anda mengizinkan hingga {limit} pengguna',
  'apiError.planLimit.languages': 'Paket Anda mengizinkan hingga {limit} bahasa konten',
  'apiError.planLimit.items': 'Paket Anda mengizinkan hingga {limit} item katalog',
  'apiError.planLimit.articles': 'Paket Anda mengizinkan hingga {limit} artikel',
  'apiError.planLimit.storage': 'Paket Anda mengizinkan penyimpanan media hingga {limit} MB. Hapus file yang tidak dipakai atau upgrade.',
  'apiError.planFeature': 'Paket Anda tidak menyertakan {feature}',
  'apiError.planArea': 'Tidak termasuk paket Anda: bagian CMS ini tidak ada di paket Anda',
  'apiError.chatHeld': '{name} sudah mengambil alih chat ini',
  'apiError.chatNotHolder': 'Ambil alih chat ini dulu',
  'apiError.chatClosed': 'Chat ini sudah ditutup',
  'apiError.chatGone': 'Chat ini sudah tidak ada (chat disimpan 7 hari)',
  'apiError.chatUnreachable': 'Chat tidak bisa dihubungi. Coba lagi sebentar lagi.',
  'apiError.tooManyRequests': 'Terlalu banyak permintaan. Coba lagi dalam semenit.',
  'apiError.tooManySignIns': 'Terlalu banyak percobaan masuk. Coba lagi dalam semenit.',
  'apiError.ssoInvalid': 'Tautan masuk ini tidak valid atau sudah kedaluwarsa',
  'apiError.suspended': 'Situs ini ditangguhkan. Silakan hubungi account manager Anda.',
  'apiError.ai.noKey': 'Penulisan AI memerlukan kunci API Anthropic. Administrator dapat mengisinya di Pengaturan.',
  'apiError.ai.unreadableKey': 'Kunci API Anthropic yang tersimpan tidak dapat dibaca. Isi ulang di Pengaturan.',
  'apiError.ai.daily': 'Batas harian penulisan AI untuk situs ini sudah tercapai. Coba lagi besok.',
  'apiError.ai.perUser': 'Terlalu banyak permintaan penulisan AI. Tunggu sebentar.',
  'apiError.ai.language': 'Bahasa itu tidak aktif di situs ini.',
  'apiError.ai.badKey': 'Anthropic menolak kunci API. Periksa kuncinya di Pengaturan.',
  'apiError.ai.noCredit': 'Saldo akun Anthropic habis. Isi ulang di console.anthropic.com.',
  'apiError.ai.rateLimited': 'Anthropic menerima terlalu banyak permintaan dari kunci ini. Coba lagi sebentar lagi.',
  'apiError.ai.overloaded': 'Layanan Anthropic sedang sibuk. Coba lagi sebentar lagi.',
  'apiError.ai.timeout': 'Anthropic tidak menjawab tepat waktu. Coba lagi.',
  'apiError.ai.network': 'Tidak dapat terhubung ke Anthropic. Coba lagi.',
  'apiError.ai.refused': 'AI menolak permintaan ini. Ubah teks atau arahannya.',
  'apiError.ai.badRequest': 'Anthropic tidak dapat memproses permintaan ini.',
  'apiError.ai.failed': 'Permintaan penulisan AI gagal. Coba lagi.',
  'status.draft': 'Draf',
  'status.published': 'Terbit',
  'status.archived': 'Diarsipkan',
  'role.admin': 'Admin',
  'role.editor': 'Editor',
  'format.bytes.b': '{n} B',
  'format.bytes.kb': '{n} KB',
  'format.bytes.mb': '{n} MB',
  'apiError.mediaNotFound': 'Media tidak ditemukan',
  'apiError.someFieldsInvalid': 'Beberapa kolom tidak valid',
  'apiError.fileTooLarge': 'Ukuran file melebihi 15 MB',
  'apiError.inquiryNotFound': 'Permintaan penawaran tidak ditemukan',
  'apiError.crossOrigin': 'Permintaan lintas asal ditolak',
  'apiError.slugUsed': 'Slug tersebut sudah dipakai',
  'apiError.missingFile': 'Kolom `file` tidak ada',
  'apiError.currentPasswordWrong': 'Kata sandi saat ini salah',
  'apiError.svgScripts': 'SVG berisi skrip atau event handler',
  'apiError.fileTypeMismatch': 'Isi file tidak sesuai dengan tipenya',
  'apiError.expectedMultipart': 'Diharapkan multipart/form-data',
  'apiError.signInRequired': 'Silakan masuk terlebih dahulu',
  'apiError.sessionExpired': 'Sesi telah berakhir',
  'apiError.badCredentials': 'Email atau kata sandi salah',
  'apiError.tooManyAttempts': 'Terlalu banyak percobaan. Coba lagi dalam 15 menit.',
  'apiError.captchaFailed': 'Cek captcha gagal. Coba lagi.',
  'apiError.captchaNotConfigured': 'Belum bisa masuk: captcha belum diatur',
  'apiError.userNotFound': 'Pengguna tidak ditemukan',
  'apiError.unknownPlatform': 'Platform tidak dikenal',
  'apiError.typeNotFound': 'Tipe konten tidak ditemukan',
  'apiError.sectionAddressTaken': 'Alamat itu sudah dipakai oleh sebuah bagian',
  'apiError.pageAddressTaken': 'Alamat itu sudah dipakai oleh sebuah halaman',
  'apiError.sectionLimit': 'Anda dapat menambah hingga 10 bagian',
  'apiError.sectionBuiltinAddress': 'Alamat bagian bawaan tidak dapat diubah',
  'apiError.sectionBuiltinDelete': 'Bagian bawaan dapat dimatikan tetapi tidak dapat dihapus',
  'apiError.sectionNotFound': 'Bagian tidak ditemukan',
  'apiError.categoryHasItems': 'Pindahkan atau hapus {n} item di kategori ini terlebih dahulu',
  'apiError.noPermission': 'Anda tidak memiliki izin untuk melakukan ini',
  'apiError.noPublish': 'Anda tidak memiliki izin untuk menerbitkan',
  'apiError.adminOnly': 'Hanya administrator yang dapat mengelola administrator',
  'apiError.ownRole': 'Anda tidak dapat mengubah peran Anda sendiri',
  'apiError.roleNotFound': 'Peran tidak ditemukan',
  'apiError.roleExists': 'Peran dengan nama ini sudah ada',
  'apiError.roleInUse': 'Peran ini masih dipakai oleh pengguna',
  'apiError.internalEndpoint': 'Endpoint internal',
  'apiError.adminRequired': 'Diperlukan peran Admin',
  'apiError.selfDemote': 'Anda tidak dapat menonaktifkan atau menurunkan peran diri sendiri',
  'apiError.emailExists': 'Pengguna dengan email ini sudah ada',
}

/** The API's fixed English error messages, by exact text. Anything else is shown as the server sent it. */
export const SERVER_MESSAGE_KEYS: Record<string, keyof typeof commonEn | keyof typeof subscriptionEn> = {
  'The subscription has expired: the CMS is read-only until the renewal is confirmed': 'apiError.subscriptionReadOnly',
  'A payment is already waiting for confirmation': 'apiError.paymentPending',
  'The proof is larger than 5 MB': 'apiError.proofTooLarge',
  'The proof must be a JPG, PNG or PDF file': 'apiError.proofType',
  'The subscription is not set up yet': 'apiError.subscriptionNotSetUp',
  'Your plan does not include this part of the CMS': 'apiError.planArea',
  'Take over the chat first': 'apiError.chatNotHolder',
  'This chat is closed': 'apiError.chatClosed',
  'This chat no longer exists (chats are kept 7 days)': 'apiError.chatGone',
  'The chat could not be reached': 'apiError.chatUnreachable',
  'Media not found': 'apiError.mediaNotFound',
  'Some fields are invalid': 'apiError.someFieldsInvalid',
  'File is larger than 15 MB': 'apiError.fileTooLarge',
  'Inquiry not found': 'apiError.inquiryNotFound',
  'Cross-origin request rejected': 'apiError.crossOrigin',
  'That slug is already used': 'apiError.slugUsed',
  'Missing `file` field': 'apiError.missingFile',
  'Current password is incorrect': 'apiError.currentPasswordWrong',
  'SVG contains scripts or event handlers': 'apiError.svgScripts',
  'File content does not match its type': 'apiError.fileTypeMismatch',
  'Expected multipart/form-data': 'apiError.expectedMultipart',
  'Sign in required': 'apiError.signInRequired',
  'Session expired': 'apiError.sessionExpired',
  'Email or password is incorrect': 'apiError.badCredentials',
  'Too many attempts. Try again in 15 minutes.': 'apiError.tooManyAttempts',
  'The captcha check failed. Try again.': 'apiError.captchaFailed',
  'Sign-in is unavailable: the captcha is not configured': 'apiError.captchaNotConfigured',
  'User not found': 'apiError.userNotFound',
  'Unknown platform': 'apiError.unknownPlatform',
  'Content type not found': 'apiError.typeNotFound',
  'That address is already used by a section': 'apiError.sectionAddressTaken',
  'That address is already used by a page': 'apiError.pageAddressTaken',
  'You can add up to 10 sections': 'apiError.sectionLimit',
  'The address of a built-in section cannot change': 'apiError.sectionBuiltinAddress',
  'Built-in sections can be switched off but not deleted': 'apiError.sectionBuiltinDelete',
  'Section not found': 'apiError.sectionNotFound',
  'You do not have permission to do this': 'apiError.noPermission',
  'You do not have permission to publish': 'apiError.noPublish',
  'Only an administrator can manage administrators': 'apiError.adminOnly',
  'You cannot change your own role': 'apiError.ownRole',
  'Role not found': 'apiError.roleNotFound',
  'A role with this name already exists': 'apiError.roleExists',
  'This role is still assigned to users': 'apiError.roleInUse',
  'Internal endpoint': 'apiError.internalEndpoint',
  'Admin role required': 'apiError.adminRequired',
  'You cannot deactivate or demote yourself': 'apiError.selfDemote',
  'A user with this email already exists': 'apiError.emailExists',
  'Too many requests. Please try again in a minute.': 'apiError.tooManyRequests',
  'Too many sign-in attempts. Try again in a minute.': 'apiError.tooManySignIns',
  'This sign-in link is invalid or has expired': 'apiError.ssoInvalid',
  'This site is suspended': 'apiError.suspended',
  'AI writing needs an Anthropic API key in Settings': 'apiError.ai.noKey',
  'The saved Anthropic API key cannot be read. Enter it again in Settings.': 'apiError.ai.unreadableKey',
  'The daily AI writing limit for this site is reached': 'apiError.ai.daily',
  'Too many AI writing requests. Wait a minute.': 'apiError.ai.perUser',
  'That language is not enabled on this site': 'apiError.ai.language',
  'Anthropic rejected the API key. Check it in Settings.': 'apiError.ai.badKey',
  'The Anthropic account has no credit left': 'apiError.ai.noCredit',
  'Anthropic is receiving too many requests from this key. Try again in a minute.': 'apiError.ai.rateLimited',
  'Anthropic is overloaded. Try again shortly.': 'apiError.ai.overloaded',
  'Anthropic did not answer in time. Try again.': 'apiError.ai.timeout',
  'Could not reach Anthropic. Try again.': 'apiError.ai.network',
  'The AI declined this request': 'apiError.ai.refused',
  'Anthropic could not process this request': 'apiError.ai.badRequest',
  'The AI writing request failed. Try again.': 'apiError.ai.failed',
}

/** The API's fixed messages that carry a value (a count, a file type), matched by shape. */
export const SERVER_MESSAGE_PATTERNS: readonly [RegExp, keyof typeof commonEn, (m: RegExpExecArray) => Record<string, string | number>][] = [
  [/^Move or delete the (\d+) product\(s\) in this category first$/, 'apiError.categoryHasProducts', (m) => ({ n: Number(m[1]) })],
  [/^Move or delete the (\d+) item\(s\) in this category first$/, 'apiError.categoryHasItems', (m) => ({ n: Number(m[1]) })],
  [/^(.+) is not allowed$/, 'apiError.typeNotAllowed', (m) => ({ type: m[1]! })],
  [/^This file is used in (\d+) places?$/, 'apiError.fileUsed', (m) => ({ count: Number(m[1]) })],
  [/^[a-z_]+ not found$/, 'apiError.notFound', () => ({})],
  [/^(.+) already took this chat$/, 'apiError.chatHeld', (m) => ({ name: m[1]! })],
  [/^Your plan allows up to (\d+) pages$/, 'apiError.planLimit.pages', (m) => ({ limit: Number(m[1]) })],
  [/^Your plan allows up to (\d+) users$/, 'apiError.planLimit.users', (m) => ({ limit: Number(m[1]) })],
  [/^Your plan allows up to (\d+) languages$/, 'apiError.planLimit.languages', (m) => ({ limit: Number(m[1]) })],
  [/^Your plan allows up to (\d+) catalogue items$/, 'apiError.planLimit.items', (m) => ({ limit: Number(m[1]) })],
  [/^Your plan allows up to (\d+) articles$/, 'apiError.planLimit.articles', (m) => ({ limit: Number(m[1]) })],
  [/^Your plan allows up to (\d+) MB of media storage$/, 'apiError.planLimit.storage', (m) => ({ limit: Number(m[1]) })],
]
